<template>
    <div class="source-detail-page">
        <div v-if="copyToastVisible" class="copy-toast">复制成功</div>
        <toast :visible="commentLikeToastVisible" :text="commentLikeToastText" />
        <AppHeader v-model="keyword" placeholder="搜索资源标题" :search-href="pageHref(1, 2000, keyword)" />
        <AppNavTabs :active-id="-1" />
        <section class="panel resource-panel">
            <div class="author-centered">
                <div class="author">
                    <img class="avatar" :src="detail.avatar" :alt="detail.nickname" />
                    <div class="author-info">
                        <div class="author-name">{{ detail.nickname }}</div>
                    </div>
                </div>
            </div>
            <h1 class="resource-title">{{ detail.title }}</h1>
            <div class="resource-meta">
                <div class="meta-item">
                    <div class="meta-label">网盘类型</div>
                    <div class="meta-value">{{ detail.driveType }}</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">文件大小</div>
                    <div class="meta-value">{{ detail.fileSize }}</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">文件数</div>
                    <div class="meta-value">{{ detail.fileCount }}</div>
                </div>
            </div>
            <div class="extract-section">
                <div class="action-panel">
                    <button class="action-btn" :class="{ ghost: !detail.curUserSubcribe }" type="button"
                        @click="handleSubscribe">
                        {{ detail.curUserSubcribe ? "已关注" : "关注" }}{{ detail.authorSubcribeCount }}
                    </button>
                    <button class="action-btn" :class="{ ghost: !detail.curUserLike }" type="button"
                        @click="handleLike">
                        {{ detail.curUserLike ? "已点赞" : "点赞" }} {{ detail.likeCount }}
                    </button>
                </div>
                <div class="extract-info">
                    <div class="extract-title">资源链接</div>
                </div>
                <div class="extract-hint">
                    <span v-if="!hasRefPayAttrs && !showLink">提示：请先提取链接</span>
                    <div v-if="extractLoading" class="extract-loading" aria-live="polite">
                        <span class="spinner" aria-hidden="true"></span>
                        正在提取中...
                    </div>
                    <div class="extract-link" :class="{ visible: showLink || hasRefPayAttrs }">
                        <span class="link-label">链接</span>
                        <a :href="displayDriveUrl" target="_blank" rel="noreferrer">{{ displayDriveUrl }}</a>
                        <button class="copy-btn" type="button" @click="copyLink">复制</button>
                    </div>
                    <div v-if="!hasRefPayAttrs" class="extract-actions">
                        <button class="extract-btn" type="button" :disabled="extractLoading" @click="handleFreeExtract">
                            {{ extractLoading ? "正在提取中..." : "免卷提取" }}
                        </button>
                        <button class="extract-btn ghost" type="button" :disabled="extractLoading"
                            @click="handlePayExtract">
                            {{ extractLoading ? "正在提取中..." : "用卷提取" }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="inviterInfo?.desc" class="inviter-card">
                <div class="inviter-title">推广口令</div>
                <p class="inviter-desc">{{ inviterInfo.desc }}</p>
                <button class="copy-btn inviter-copy-btn" type="button" @click="copyInviterDesc">复制口令</button>
            </div>
        </section>

        <section class="panel comment-panel">
            <div class="comment-header">
                <div class="comment-title">评论区</div>
                <div class="comment-count">{{ comments.length }} 条评论</div>
            </div>
            <div class="comment-input">
                <textarea v-model="commentText" rows="3" placeholder="写下你的评论..." />
                <div class="comment-tools">
                    <button type="button" class="emoji-toggle" @click="toggleEmojiPanel('comment')">表情</button>
                </div>
                <div v-if="emojiPanelVisible && emojiTarget === 'comment'" class="emoji-panel">
                    <div class="emoji-panel-header">
                        <span>选择表情</span>
                        <button type="button" class="emoji-close" @click="closeEmojiPanel">×</button>
                    </div>
                    <div class="emoji-grid">
                        <button v-for="emoji in emojiPageItems" :key="emoji" type="button" class="emoji-btn"
                            @click="insertEmoji(emoji)">
                            {{ emoji }}
                        </button>
                    </div>
                    <div class="emoji-pagination">
                        <button type="button" class="emoji-page-btn" :disabled="emojiPage === 1" @click="prevEmojiPage">
                            上一页
                        </button>
                        <span class="emoji-page-info">{{ emojiPage }} / {{ emojiPageCount }}</span>
                        <button type="button" class="emoji-page-btn" :disabled="emojiPage === emojiPageCount"
                            @click="nextEmojiPage">
                            下一页
                        </button>
                    </div>
                </div>
                <button type="button" class="comment-submit" :disabled="commentSubmitting" @click="submitComment">
                    {{ commentSubmitting ? "发布中..." : "发布" }}
                </button>
            </div>
            <div class="comment-list">
                <article v-for="comment in comments" :key="comment.id" class="comment-item">
                    <img class="comment-avatar" :src="comment.avatar" :alt="comment.user" />
                    <div class="comment-body">
                        <div class="comment-meta">
                            <span class="comment-user">{{ comment.user }}</span>
                            <span class="comment-time">{{ comment.time }}</span>
                        </div>
                        <p class="comment-content">
                            <span v-if="comment.replyTo" class="comment-mention">@{{ comment.replyTo }}</span>
                            {{ comment.content }}
                        </p>
                        <div class="comment-actions">
                            <button type="button" class="comment-like" :class="{ active: comment.isLiked }"
                                :disabled="comment.likePending" @click="toggleCommentLike(comment.id)">
                                点赞 {{ comment.likes }}
                            </button>
                            <button type="button" class="comment-reply" @click="openReply(comment)">回复</button>
                            <button v-if="comment.canDelete" type="button" class="comment-delete"
                                @click="deleteComment(comment.id)">
                                删除
                            </button>
                        </div>
                    </div>
                </article>
            </div>
            <button v-if="hasMoreComments" type="button" class="comment-more-btn" :disabled="commentLoading"
                @click="loadMoreComments">
                {{ commentLoading ? "加载中..." : "加载更多" }}
            </button>
        </section>

        <div v-if="replyVisible" class="reply-sheet">
            <div class="reply-sheet-mask" @click="closeReply"></div>
            <div class="reply-sheet-panel">
                <div class="reply-sheet-header">回复 @{{ replyTarget?.name || "" }}</div>
                <textarea v-model="replyText" rows="3" class="reply-textarea" placeholder="写下你的回复..." />
                <div class="reply-tools">
                    <button type="button" class="emoji-toggle" @click="toggleEmojiPanel('reply')">表情</button>
                </div>
                <div v-if="emojiPanelVisible && emojiTarget === 'reply'" class="emoji-panel">
                    <div class="emoji-panel-header">
                        <span>选择表情</span>
                        <button type="button" class="emoji-close" @click="closeEmojiPanel">×</button>
                    </div>
                    <div class="emoji-grid">
                        <button v-for="emoji in emojiPageItems" :key="emoji" type="button" class="emoji-btn"
                            @click="insertEmoji(emoji)">
                            {{ emoji }}
                        </button>
                    </div>
                    <div class="emoji-pagination">
                        <button type="button" class="emoji-page-btn" :disabled="emojiPage === 1" @click="prevEmojiPage">
                            上一页
                        </button>
                        <span class="emoji-page-info">{{ emojiPage }} / {{ emojiPageCount }}</span>
                        <button type="button" class="emoji-page-btn" :disabled="emojiPage === emojiPageCount"
                            @click="nextEmojiPage">
                            下一页
                        </button>
                    </div>
                </div>
                <div class="reply-actions">
                    <button type="button" class="reply-cancel" @click="closeReply">取消</button>
                    <button type="button" class="reply-submit" :disabled="replySubmitting" @click="submitReply">
                        {{ replySubmitting ? "发送中..." : "发送" }}
                    </button>
                </div>
            </div>
        </div>

        <AppFooter active="home" />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, onMounted } from "vue";
import { Modal, message } from "ant-design-vue";
import {
    $api_Comment_AddComment,
    $api_Comment_DeleteComment,
    $api_Comment_GetCommentsBySourceIdPage,
} from "~/composables/comment";
import { normalizeAvatarUrl } from "~/composables/user";
import { $api_Source_Action, $api_Source_FreePaySource, $api_Source_PaySource, driverMap } from "~/composables/source";
import emojiData from "~/assets/emoji.json";

onMounted(() => {
    if (!import.meta.client) return;
    const channelCode = route.query.channelCode;
    const inviterId = route.query.inviterId;
    const inviterIdValue = Array.isArray(inviterId) ? inviterId[0] : inviterId;
    const channelCodeValue = Array.isArray(channelCode) ? channelCode[0] : channelCode;

    if (channelCodeValue) {
        console.log("channelCodeValue=" + channelCodeValue)
        window.localStorage.setItem("channelCode", String(channelCodeValue));
    }
    if (inviterIdValue) {
        console.log("inviterId=" + inviterId)
        window.localStorage.setItem("inviterId", String(inviterIdValue));
    }
});

const showLink = ref(false);
const commentText = ref("");
const keyword = ref("");
const copyToastVisible = ref(false);
const commentLikeToastVisible = ref(false);
const commentLikeToastText = ref("");
let copyToastTimer: ReturnType<typeof setTimeout> | null = null;
let commentLikeToastTimer: ReturnType<typeof setTimeout> | null = null;

const route = useRoute();
const config = useRuntimeConfig();

type SourceAttr = {
    attrType?: number;
    attrData?: string;
};

type SourceDetail = {
    userId?: string;
    userHeadUrl?: string;
    nickName?: string;
    sourceId?: string;
    title?: string;
    attrs?: SourceAttr[];
    driverTypes?: number[];
    likeCount?: number;
    authorSubcribeCount?: number;
    curUserLike?: boolean;
    curUserSubcribe?: boolean;
};

type CommentItem = {
    commentId?: string;
    content?: string;
    userId?: number;
    nickName?: string;
    userHeadUrl?: string;
    refId?: string;
    refUserId?: number;
    refNickName?: string;
    refUserHeadUrl?: string;
    sourceId?: string;
    isTop?: number;
    likeCount?: number;
    createTime?: string;
    state?: number;
    children?: string;
    canDelete?: boolean;
};

type CommentPage = {
    total?: number;
    currentNumber?: number;
    pageSize?: number;
    list?: CommentItem[];
    extend?: string;
};

type CommentResponse = {
    code: number;
    data?: CommentPage;
    message?: string;
    busCode?: string;
};

type AddCommentResponse = {
    code: number;
    data?: CommentItem;
    message?: string;
    busCode?: string;
};

type CommentLocal = CommentItem & {
    isLiked?: boolean;
    likePending?: boolean;
};

const sourceId = computed<string>(() => {
    const raw = route.params.id;
    const value = Array.isArray(raw) ? raw[0] : raw;
    return value ? String(value) : "";
});

const { data } = await api_Source_GetSouceById({
    method: "GET",
    key: "api_Source_GetSouceById" + sourceId.value,
    query: { sourceId: sourceId.value },
    server: true,
})

console.log('sourceinfo', data.value)

const commentPageSize = 20;
const { data: commentData } = await useAsyncData(
    `source-comments-${sourceId.value}`,
    () =>
        $api_Comment_GetCommentsBySourceIdPage({
            method: "POST",
            body: {
                pageNum: 1,
                pageSize: commentPageSize,
                sourceId: sourceId.value || 0,
                states: [1],
                refCommentId: 0,
            },
        }) as Promise<CommentResponse>,
    { server: true }
);
const commentList = ref<CommentLocal[]>([]);
const commentTotal = ref(0);
const commentPage = ref(1);
const commentLoading = ref(false);
const commentSubmitting = ref(false);
const extractLoading = ref(false);
const replyVisible = ref(false);
const replyText = ref("");
const replySubmitting = ref(false);
const replyTarget = ref<{ id: string; name: string } | null>(null);
const emojiPanelVisible = ref(false);
const emojiTarget = ref<"comment" | "reply">("comment");
const emojiPage = ref(1);
const emojiPageSize = 80;
const allEmoji = Array.isArray(emojiData)
    ? (emojiData as Array<{ char?: string }>)
        .map((item) => item.char)
        .filter((item): item is string => Boolean(item))
    : [];
const emojiPageCount = computed(() => Math.max(1, Math.ceil(allEmoji.length / emojiPageSize)));
const emojiPageItems = computed(() => {
    const start = (emojiPage.value - 1) * emojiPageSize;
    return allEmoji.slice(start, start + emojiPageSize);
});

watch(
    commentData,
    (response) => {
        if (!response || (response.code !== 0 && response.code !== 1)) {
            commentList.value = [];
            commentTotal.value = 0;
            commentPage.value = 1;
            return;
        }
        commentList.value = (response.data?.list ?? []).map((item) => ({
            ...item,
            commentId: item.commentId ? String(item.commentId) : "",
            refId: item.refId ? String(item.refId) : "",
            isLiked: false,
            likePending: false,
        }));
        commentTotal.value = response.data?.total ?? commentList.value.length;
        commentPage.value = response.data?.currentNumber ?? 1;
    },
    { immediate: true }
);

const hasMoreComments = computed(() => {
    return commentList.value.length < commentTotal.value;
});

const loadMoreComments = async () => {
    if (commentLoading.value || !hasMoreComments.value) return;
    commentLoading.value = true;
    const nextPage = commentPage.value + 1;
    try {
        const response = await $api_Comment_GetCommentsBySourceIdPage({
            method: "POST",
            body: {
                pageNum: nextPage,
                pageSize: commentPageSize,
                sourceId: sourceId.value || 0,
                states: [1],
                refCommentId: 0,
            },
        }) as CommentResponse;
        if (!response) return;
        if (response.code !== 0 && response.code !== 1) return;
        const nextList = (response.data?.list ?? []).map((item) => ({
            ...item,
            commentId: item.commentId ? String(item.commentId) : "",
            refId: item.refId ? String(item.refId) : "",
            isLiked: false,
            likePending: false,
        }));
        if (nextList.length > 0) {
            commentList.value = [...commentList.value, ...nextList];
        }
        commentTotal.value = response.data?.total ?? commentTotal.value;
        commentPage.value = response.data?.currentNumber ?? nextPage;
    } catch (error) {
        console.error("load comments failed", error);
    } finally {
        commentLoading.value = false;
    }
};

const submitComment = async () => {
    const content = commentText.value.trim();
    if (!content || commentSubmitting.value) return;
    commentSubmitting.value = true;
    try {
        const response = await $api_Comment_AddComment({
            method: "POST",
            body: {
                content,
                refId: 0,
                sourceId: sourceId.value || 0,
                fileKeys: [],
            },
        }) as AddCommentResponse;
        if (!response) return;
        if (response.code === -1) {
            showCommentLikeToast(response.message || "评论失败");
            return;
        }
        if (response.code !== 0 && response.code !== 1) return;
        if (response.data) {
            commentList.value = [
                {
                    ...response.data,
                    commentId: response.data.commentId ? String(response.data.commentId) : "",
                    refId: response.data.refId ? String(response.data.refId) : "",
                    canDelete: true,
                    isLiked: false,
                    likePending: false,
                },
                ...commentList.value,
            ];
        }
        commentTotal.value += 1;
        commentText.value = "";
    } catch (error) {
        console.error("add comment failed", error);
    } finally {
        commentSubmitting.value = false;
    }
};

const openReply = (comment: { id: string; user: string }) => {
    if (!comment?.id) return;
    replyTarget.value = { id: comment.id, name: comment.user || "匿名用户" };
    replyText.value = "";
    replyVisible.value = true;
};

const closeReply = () => {
    replyVisible.value = false;
    replyText.value = "";
    replyTarget.value = null;
    if (emojiTarget.value === "reply") {
        emojiPanelVisible.value = false;
    }
};

const submitReply = async () => {
    const content = replyText.value.trim();
    const target = replyTarget.value;
    if (!content || replySubmitting.value || !target?.id) return;
    replySubmitting.value = true;
    try {
        const response = await $api_Comment_AddComment({
            method: "POST",
            body: {
                content,
                refId: target.id,
                sourceId: sourceId.value || 0,
                fileKeys: [],
            },
        }) as AddCommentResponse;
        if (!response) return;
        if (response.code === -1) {
            showCommentLikeToast(response.message || "评论失败");
            return;
        }
        if (response.code !== 0 && response.code !== 1) return;
        if (response.data) {
            commentList.value = [
                {
                    ...response.data,
                    commentId: response.data.commentId ? String(response.data.commentId) : "",
                    canDelete: true,
                    refId: target.id,
                    refNickName: target.name,
                    isLiked: false,
                    likePending: false,
                },
                ...commentList.value,
            ];
        }
        commentTotal.value += 1;
        closeReply();
    } catch (error) {
        console.error("reply comment failed", error);
    } finally {
        replySubmitting.value = false;
    }
};

const toggleEmojiPanel = (target: "comment" | "reply") => {
    if (emojiPanelVisible.value && emojiTarget.value === target) {
        emojiPanelVisible.value = false;
        return;
    }
    emojiTarget.value = target;
    emojiPage.value = 1;
    emojiPanelVisible.value = true;
};

const closeEmojiPanel = () => {
    emojiPanelVisible.value = false;
};

const prevEmojiPage = () => {
    if (emojiPage.value > 1) {
        emojiPage.value -= 1;
    }
};

const nextEmojiPage = () => {
    if (emojiPage.value < emojiPageCount.value) {
        emojiPage.value += 1;
    }
};

const insertEmoji = (emoji: string) => {
    if (emojiTarget.value === "comment") {
        commentText.value += emoji;
    } else {
        replyText.value += emoji;
    }
};

const showCommentLikeToast = (text: string) => {
    commentLikeToastText.value = text;
    commentLikeToastVisible.value = true;
    if (commentLikeToastTimer) {
        clearTimeout(commentLikeToastTimer);
    }
    commentLikeToastTimer = setTimeout(() => {
        commentLikeToastVisible.value = false;
        commentLikeToastTimer = null;
    }, 3000);
};

const deleteComment = async (commentId: string) => {
    if (!commentId) return;
    try {
        const response = await $api_Comment_DeleteComment({
            method: "POST",
            body: { commentId },
        }) as { code?: number; data?: boolean; message?: string };
        if (!response) return;
        if (response.code === -1) {
            showCommentLikeToast(response.message || "删除失败");
            return;
        }
        if (response.code !== 0 && response.code !== 1) return;
        if (!response.data) return;
        const index = commentList.value.findIndex((item) => item.commentId === commentId);
        if (index === -1) return;
        commentList.value = commentList.value.filter((item) => item.commentId !== commentId);
        commentTotal.value = Math.max(0, commentTotal.value - 1);
    } catch (error) {
        console.error("delete comment failed", error);
    }
};

const toggleCommentLike = async (commentId: string) => {
    if (!commentId) return;
    const index = commentList.value.findIndex((item) => item.commentId === commentId);
    if (index === -1) return;
    const target = commentList.value[index];
    if (target.likePending) return;
    commentList.value[index] = { ...target, likePending: true };
    try {
        const response = await $api_Source_Action({
            method: "POST",
            body: { id: commentId, type: 21 },
        }) as { data?: number; code?: number; message?: string };
        if (response?.code === -1) {
            showCommentLikeToast(response.message || "点赞失败");
            return;
        }
        if (response?.data !== 0 && response?.data !== 1) return;
        const isLiked = response.data === 1;
        const likeCount = target.likeCount ?? 0;
        commentList.value[index] = {
            ...commentList.value[index],
            isLiked,
            likeCount: Math.max(0, likeCount + (isLiked ? 1 : -1)),
        };
    } catch (error) {
        console.error("comment like failed", error);
    } finally {
        commentList.value[index] = {
            ...commentList.value[index],
            likePending: false,
        };
    }
};

type InviterInfo = {
    inviterId?: string;
    channelCode?: string | null;
    desc?: string;
    shortUrl?: string;
};

type SourceDetailResponse = {
    code: number;
    data?: {
        source?: SourceDetail;
        curUserRefPayInfo?: { userId?: string; attrs?: any };
        curInviterInfo?: InviterInfo;
    };
};

const sourceDetail = ref<SourceDetail | null>(
    (data.value as SourceDetailResponse | undefined)?.data?.source ?? null
);
const refPayInfo = ref<{ userId?: string; attrs?: any } | null>(
    (data.value as SourceDetailResponse | undefined)?.data?.curUserRefPayInfo ?? null
);
const inviterInfo = ref<InviterInfo | null>(
    (data.value as SourceDetailResponse | undefined)?.data?.curInviterInfo ?? null
);
watch(
    () => (data.value as SourceDetailResponse | undefined)?.data?.source,
    (value) => {
        sourceDetail.value = value ?? null;
    }
);
watch(
    () => (data.value as SourceDetailResponse | undefined)?.data?.curUserRefPayInfo,
    (value) => {
        refPayInfo.value = value ?? null;
    }
);
watch(
    () => (data.value as SourceDetailResponse | undefined)?.data?.curInviterInfo,
    (value) => {
        inviterInfo.value = value ?? null;
    }
);

const getDrive = (attrs: SourceAttr[] | undefined, driverTypes: number[] | undefined) => {
    let driveTypeLabel = "";
    if (Array.isArray(driverTypes) && driverTypes.length > 0) {
        const match = driverMap.find((item) => item.attrType === driverTypes[0]);
        driveTypeLabel = match?.driverName || "";
    }
    if (!attrs?.length) return { driveUrl: "", driveType: driveTypeLabel };
    const match = attrs.find(
        (item) =>
            typeof item.attrType === "number" &&
            item.attrType > 2000 &&
            item.attrType < 3000 &&
            item.attrData
    );
    if (match?.attrData) {
        if (!driveTypeLabel) {
            const typeMatch = driverMap.find((item) => item.attrType === match.attrType);
            driveTypeLabel = typeMatch?.driverName || "";
        }
        return { driveUrl: match.attrData, driveType: driveTypeLabel };
    }
    return { driveUrl: "", driveType: driveTypeLabel };
};

const detail = computed(() => {
    const item = sourceDetail.value;
    const driveInfo = getDrive(item?.attrs, item?.driverTypes);
    return {
        nickname: item?.nickName || "匿名分享",
        avatar:
            normalizeAvatarUrl(item?.userHeadUrl, config) ||
            "https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=120&q=80",
        title: item?.title || "",
        driveType: driveInfo.driveType || "未知网盘",
        fileSize: "-",
        fileCount: "-",
        driveUrl: driveInfo.driveUrl || "",
        authorSubcribeCount: item?.authorSubcribeCount ?? 0,
        likeCount: item?.likeCount ?? 0,
        curUserLike: item?.curUserLike ?? false,
        curUserSubcribe: item?.curUserSubcribe ?? false,
    };
});

const seoTitle = computed(() => {
    const title = detail.value.title?.trim() || "资源详情";
    const driveType = detail.value.driveType?.trim() || "网盘";
    return `爱分享-【${title}】-` + driveType;
});

const seoDescription = computed(() => {
    return detail.value.title?.trim() || "资源详情";
});

const seoKeywords = computed(() => {
    const title = detail.value.title?.trim() || "资源详情";
    const driveType = detail.value.driveType?.trim() || "网盘";
    return `${driveType},${title},爱分享`;
});

useSeoMeta({
    title: seoTitle,
    ogTitle: seoTitle,
    description: seoDescription,
    ogDescription: seoDescription,
});

useHead({
    meta: [
        { name: "keywords", content: seoKeywords },
    ],
});

const hasRefPayAttrs = computed(() => {
    const attrs = refPayInfo.value?.attrs;
    return Array.isArray(attrs) && attrs.length > 0;
});

const displayDriveUrl = computed(() => {
    if (hasRefPayAttrs.value) {
        const attrs = refPayInfo.value?.attrs as Array<{ attrData?: string }> | undefined;
        return attrs?.[0]?.attrData || "";
    }
    return detail.value.driveUrl;
});

const handleSubscribe = async () => {
    const item = sourceDetail.value;
    const userId = item?.userId;
    if (!userId) return;
    try {
        const response = await $api_Source_Action({
            method: "POST",
            body: { id: userId, type: 31 },
        }) as { data?: number };
        if (response?.data !== 0 && response?.data !== 1) return;
        const nextSubscribed = response.data === 1;
        if (!sourceDetail.value) return;
        const currentCount = sourceDetail.value.authorSubcribeCount ?? 0;
        const delta = nextSubscribed ? 1 : -1;
        sourceDetail.value = {
            ...sourceDetail.value,
            curUserSubcribe: nextSubscribed,
            authorSubcribeCount: Math.max(0, currentCount + delta),
        };
    } catch (error) {
        console.error("subscribe failed", error);
    }
};

const handleLike = async () => {
    const item = sourceDetail.value;
    const id = item?.sourceId || sourceId.value;
    if (!id) return;
    try {
        const response = await $api_Source_Action({
            method: "POST",
            body: { id, type: 11 },
        }) as { data?: number; code?: number; message?: string };
        if (response?.code === -1) {
            showCommentLikeToast(response.message || "点赞失败");
            return;
        }
        if (response?.data !== 0 && response?.data !== 1) return;
        const nextLiked = response.data === 1;
        if (!sourceDetail.value) return;
        const currentCount = sourceDetail.value.likeCount ?? 0;
        const delta = nextLiked ? 1 : -1;
        sourceDetail.value = {
            ...sourceDetail.value,
            curUserLike: nextLiked,
            likeCount: Math.max(0, currentCount + delta),
        };
    } catch (error) {
        console.error("like failed", error);
    }
};

const handleFreeExtract = async () => {
    const id = sourceDetail.value?.sourceId || sourceId.value;
    if (!id) return;
    if (extractLoading.value) return;
    extractLoading.value = true;
    try {
        const response = await $api_Source_FreePaySource({
            method: "GET",
            query: { sourceId: id },
        }) as { code?: number; data?: SourceAttr[]; message?: string };
        if (response?.code === 1 && Array.isArray(response.data) && response.data.length > 0) {
            refPayInfo.value = {
                ...refPayInfo.value,
                attrs: response.data,
            };
            showLink.value = true;
            return;
        }
        Modal.info({
            title: "提示",
            content: response?.message || "提取失败",
            okText: "知道了",
        });
    } catch (error) {
        console.error("free extract failed", error);
        Modal.info({
            title: "提示",
            content: "提取失败",
            okText: "知道了",
        });
    } finally {
        extractLoading.value = false;
    }

};

const handlePayExtract = async () => {
    const id = sourceDetail.value?.sourceId || sourceId.value;
    if (!id) return;
    if (extractLoading.value) return;
    extractLoading.value = true;
    try {
        const response = await $api_Source_PaySource({
            method: "GET",
            query: { sourceId: id },
        }) as { code?: number; data?: SourceAttr[]; message?: string };
        if (response?.code === 1 && Array.isArray(response.data) && response.data.length > 0) {
            refPayInfo.value = {
                ...refPayInfo.value,
                attrs: response.data,
            };
            showLink.value = true;
            return;
        }
        Modal.info({
            title: "提示",
            content: response?.message || "提取失败",
            okText: "知道了",
        });
    } catch (error) {
        console.error("pay extract failed", error);
        Modal.info({
            title: "提示",
            content: "提取失败",
            okText: "知道了",
        });
    } finally {
        extractLoading.value = false;
    }

};

const comments = computed(() => {
    return commentList.value.map((item) => ({
        id: item.commentId ?? "",
        user: item.nickName || "匿名用户",
        avatar:
            normalizeAvatarUrl(item.userHeadUrl, config) ||
            "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=120&q=80",
        time: (item.createTime || "").replace("T", " ").slice(0, 16),
        content: item.content || "",
        replyTo: item.refNickName || "",
        likes: item.likeCount ?? 0,
        isLiked: item.isLiked ?? false,
        likePending: item.likePending ?? false,
        canDelete: item.canDelete ?? false,
    }));
});

const pageHref = (_targetPage: number, _attrType: number, likeTitle = "") => {
    const query = new URLSearchParams();
    if (likeTitle) {
        query.set("likeTitle", likeTitle);
    }
    const qs = query.toString();
    return qs ? `/?${qs}` : "/";
};

const copyText = async (text: string) => {
    if (!text) return;
    if (!import.meta.client) {
        return;
    }
    try {
        const canUseClipboard =
            typeof navigator !== "undefined" &&
            !!navigator.clipboard?.writeText;
        if (canUseClipboard) {
            await navigator.clipboard.writeText(text);
        } else if (typeof document !== "undefined") {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.setAttribute("readonly", "true");
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        } else {
            return;
        }
        copyToastVisible.value = true;
        if (copyToastTimer) {
            clearTimeout(copyToastTimer);
        }
        copyToastTimer = setTimeout(() => {
            copyToastVisible.value = false;
            copyToastTimer = null;
        }, 3000);
    } catch (error) {
        console.error("copy failed", error);
    }
};

const copyLink = async () => {
    await copyText(displayDriveUrl.value);
};

const copyInviterDesc = async () => {
    await copyText(inviterInfo.value?.desc || "");
};

onBeforeUnmount(() => {
    if (copyToastTimer) {
        clearTimeout(copyToastTimer);
    }
    if (commentLikeToastTimer) {
        clearTimeout(commentLikeToastTimer);
    }
});
</script>

<style scoped>
.source-detail-page {
    --ink: #1f1b20;
    --muted: #6b5f63;
    --accent: #ff7c5b;
    --soft: #fff4ee;
    --card: #ffffff;
    --shadow: rgba(243, 117, 82, 0.18);
    min-height: 100vh;
    padding: calc(var(--app-header-height, 50px) + var(--app-navtabs-height, 40px)) 14px 90px;
    background:
        radial-gradient(circle at 10% 0%, rgba(255, 215, 196, 0.9), transparent 52%),
        radial-gradient(circle at 85% 0%, rgba(255, 233, 215, 0.8), transparent 46%),
        linear-gradient(180deg, #f9f3ec 0%, #fff 70%);
    color: var(--ink);
    display: grid;
    gap: 14px;
}

.copy-toast {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(31, 27, 32, 0.92);
    color: #fff;
    padding: 10px 16px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    z-index: 9999;
}

.panel {
    background: var(--card);
    border-radius: 18px;
    padding: 16px;
    box-shadow: 0 16px 30px var(--shadow);
    display: grid;
    gap: 12px;
    animation: rise 0.6s ease both;
}

.author-centered {
    display: flex;
    justify-content: center;
}

.author {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
}

.avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #ffe1d4;
}

.author-info {
    display: grid;
    gap: 4px;
}

.author-name {
    font-size: 16px;
    font-weight: 700;
}

.author-followers {
    font-size: 12px;
    color: var(--muted);
}

.resource-title {
    margin: 0;
    font-size: 20px;
    line-height: 1.4;
}

.resource-meta {
    display: grid;
    gap: 10px;
    background: var(--soft);
    padding: 12px;
    border-radius: 14px;
}

.meta-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
}

.meta-label {
    font-size: 12px;
    color: var(--muted);
}

.meta-value {
    font-size: 14px;
    font-weight: 700;
}

.extract-section {
    display: grid;
    gap: 12px;
    background: linear-gradient(135deg, #fff, #fff7f1);
    padding: 12px;
    border-radius: 14px;
}

.extract-info {
    display: grid;
    gap: 6px;
}

.extract-title {
    font-size: 16px;
    font-weight: 700;
}

.extract-desc {
    margin: 0;
    font-size: 12px;
    color: var(--muted);
}

.extract-btn {
    border: none;
    background: var(--accent);
    color: #fff;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    width: 120px;
}

.extract-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
}

.extract-btn.ghost {
    background: #fff;
    color: var(--ink);
    border: 1px solid #ffd2c6;
}

.extract-link {
    display: none;
    gap: 10px;
    align-items: center;
    background: #fff0ea;
    padding: 10px 12px;
    border-radius: 12px;
    word-break: break-all;
    font-size: 12px;
}

.extract-link.visible {
    display: flex;
}

.extract-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff6f1;
    border-radius: 12px;
    padding: 10px 12px;
    color: #8c6f69;
    font-size: 12px;
    font-weight: 600;
}

.spinner {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid #f3c7ba;
    border-top-color: #e95b3b;
    animation: spin 0.9s linear infinite;
}

.link-label {
    font-size: 11px;
    color: var(--muted);
    white-space: nowrap;
}

.extract-link a {
    color: #e95b3b;
    text-decoration: none;
}

.extract-link {
    flex-wrap: wrap;
}

.copy-btn {
    border: 1px solid #f3c7ba;
    background: #fff;
    color: #8c6f69;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
}

.extract-hint {
    border: 1px dashed #e2d6d0;
    border-radius: 12px;
    padding: 10px 12px;
    color: #9a8f8c;
    font-size: 12px;
    background: #fffafa;
    display: grid;
    gap: 10px;
}

.inviter-card {
    display: grid;
    gap: 10px;
    background: #fff7f1;
    padding: 12px;
    border-radius: 14px;
    border: 1px dashed #f3c7ba;
}

.inviter-title {
    font-size: 14px;
    font-weight: 700;
}

.inviter-desc {
    margin: 0;
    font-size: 12px;
    color: var(--ink);
    line-height: 1.5;
    white-space: pre-wrap;
}

.inviter-copy-btn {
    justify-self: start;
}


.action-panel {
    display: flex;
    gap: 12px;
    justify-content: center;
}

.action-btn {
    flex: 0 0 110px;
    border: none;
    background: var(--accent);
    color: #fff;
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    justify-content: center;
    gap: 4px;
}

.action-btn.ghost {
    background: #fff;
    color: var(--ink);
    border: 1px solid #ffd2c6;
}

.comment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.comment-title {
    font-size: 16px;
    font-weight: 700;
}

.comment-count {
    font-size: 12px;
    color: var(--muted);
}

.comment-input {
    display: grid;
    gap: 10px;
}

.comment-input textarea {
    border: 1px solid #f0e4de;
    border-radius: 12px;
    padding: 10px;
    font-size: 13px;
    resize: vertical;
}

.comment-tools {
    display: flex;
    justify-content: flex-start;
}

.comment-submit {
    border: none;
    background: #2b2529;
    color: #fff;
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    width: 96px;
}

.comment-more-btn {
    border: 1px solid #f0e4de;
    background: #fff;
    color: #6b5f63;
    padding: 8px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    justify-self: center;
}

.comment-more-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.comment-list {
    display: grid;
    gap: 14px;
}

.comment-item {
    display: flex;
    gap: 12px;
}

.comment-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #ffe1d4;
}

.comment-body {
    display: grid;
    gap: 6px;
}

.comment-meta {
    display: flex;
    gap: 10px;
    align-items: center;
}

.comment-user {
    font-size: 13px;
    font-weight: 700;
}

.comment-time {
    font-size: 11px;
    color: var(--muted);
}

.comment-content {
    margin: 0;
    font-size: 13px;
    color: #2b2529;
}

.comment-actions {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: var(--muted);
}

.emoji-toggle {
    border: 1px solid #f0e4de;
    background: #fff;
    color: #6b5f63;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.emoji-panel {
    border: 1px solid #f0e4de;
    background: #fff;
    border-radius: 14px;
    padding: 10px;
    display: grid;
    gap: 10px;
    max-height: 40vh;
}

.emoji-panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    font-weight: 700;
    color: #6b5f63;
}

.emoji-close {
    border: none;
    background: transparent;
    font-size: 16px;
    cursor: pointer;
    color: #6b5f63;
}

.emoji-grid {
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 6px;
    overflow: auto;
    max-height: 28vh;
    padding-right: 4px;
}

.emoji-btn {
    border: 1px solid #f0e4de;
    background: #fff;
    border-radius: 8px;
    padding: 6px 0;
    font-size: 16px;
    cursor: pointer;
    line-height: 1;
}

.emoji-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: #6b5f63;
}

.emoji-page-btn {
    border: 1px solid #f0e4de;
    background: #fff;
    color: #6b5f63;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.emoji-page-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.comment-like {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 11px;
    color: var(--muted);
    cursor: pointer;
}

.comment-reply {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 11px;
    color: var(--muted);
    cursor: pointer;
}

.comment-delete {
    border: none;
    background: transparent;
    padding: 0;
    font-size: 11px;
    color: #e04848;
    cursor: pointer;
}

.comment-like.active {
    color: #d35b47;
    font-weight: 700;
}

.comment-like:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.comment-mention {
    color: #fb7299;
    font-weight: 700;
    margin-right: 4px;
}

.reply-sheet {
    position: fixed;
    inset: 0;
    z-index: 10000;
}

.reply-sheet-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
}

.reply-sheet-panel {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    padding: 16px;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -12px 30px rgba(0, 0, 0, 0.12);
    display: grid;
    gap: 12px;
}

.reply-sheet-header {
    font-size: 14px;
    font-weight: 700;
    color: #fb7299;
}

.reply-textarea {
    border: 1px solid #f0e4de;
    border-radius: 12px;
    padding: 10px;
    font-size: 13px;
    resize: vertical;
}

.reply-tools {
    display: flex;
    justify-content: flex-start;
}

.reply-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.reply-cancel {
    border: 1px solid #f0e4de;
    background: #fff;
    color: #6b5f63;
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.reply-submit {
    border: none;
    background: #fb7299;
    color: #fff;
    padding: 8px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.reply-submit:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

@keyframes rise {
    from {
        opacity: 0;
        transform: translateY(12px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (min-width: 768px) {
    .source-detail-page {
        padding: 28px 22% 120px;
    }
}
</style>
